import httpApi from "@src/utils/httpApi";
import {
  ICuratorConversation,
  ICuratorConversationStatus,
} from "@src/types/curator";

export type CuratorConversationsMeta = {
  pagination?: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
  };
};

export type CuratorConversationsResponse = {
  data: ICuratorConversation[];
  meta?: CuratorConversationsMeta;
};

export const getCuratorConversations = async (
  status?: ICuratorConversationStatus
): Promise<ICuratorConversation[]> => {
  try {
    const response = await httpApi.get<CuratorConversationsResponse>(
      "/api/curator-conversations",
      {
        params: status ? { status } : undefined,
      }
    );
    return response.data.data || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error fetching curator conversations", error);
    return [];
  }
};

export const updateConversationStatus = async (
  conversationId: string | number,
  status: "active" | "blocked" | "ai_blocked" | "ended"
) => {
  try {
    const response = await httpApi.put(
      `/api/curator-conversations/${conversationId}`,
      {
        data: { status },
      }
    );
    return response.data.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error updating conversation status", error);
    throw error;
  }
};

export const deleteConversation = async (conversationId: string | number) => {
  try {
    const response = await httpApi.delete(
      `/api/curator-conversations/${conversationId}`
    );
    return response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error deleting conversation", error);
    throw error;
  }
};

export const getCuratorSettings = async (
  curatorId: string | number
): Promise<{ receiveMessages?: boolean; aiModeration?: boolean }> => {
  const response = await httpApi.get(`/api/curators/${curatorId}`, {
    params: { "fields[0]": "receiveMessages", "fields[1]": "aiModeration" },
  });
  return response.data?.data ?? {};
};

export const updateCuratorSettings = async (
  curatorId: string | number,
  settings: { receiveMessages?: boolean; aiModeration?: boolean }
) => {
  try {
    const response = await httpApi.put(`/api/curators/${curatorId}`, {
      data: settings,
    });
    return response.data.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error updating curator settings", error);
    throw error;
  }
};

export const acceptConversation = async (conversationId: string | number) => {
  try {
    const response = await httpApi.post(
      `/api/curator-conversations/${conversationId}/accept`
    );
    return response.data.data ?? response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error accepting conversation", error);
    throw error;
  }
};

export const endConversation = async (conversationId: string | number) => {
  try {
    const response = await httpApi.post(
      `/api/curator-conversations/${conversationId}/end`
    );
    return response.data.data ?? response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error ending conversation", error);
    throw error;
  }
};

export const blockConversation = async (conversationId: string | number) => {
  try {
    const response = await httpApi.post(
      `/api/curator-conversations/${conversationId}/block`
    );
    return response.data.data ?? response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error blocking conversation", error);
    throw error;
  }
};

export const unblockConversation = async (conversationId: string | number) => {
  try {
    const response = await httpApi.post(
      `/api/curator-conversations/${conversationId}/unblock`
    );
    return response.data.data ?? response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error unblocking conversation", error);
    throw error;
  }
};

export const markConversationRead = async (conversationId: string | number) => {
  try {
    const response = await httpApi.post(
      `/api/curator-conversations/${conversationId}/read`
    );
    return response.data.data ?? response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error marking conversation as read", error);
    throw error;
  }
};
